import { Injectable } from '@angular/core';
import { forkJoin, Observable, of } from 'rxjs';
import { map, take, tap } from 'rxjs/operators';
import { NotificationTask } from 'src/app/classes';
import { CustomerService } from '../../customer/services/customer.service';
import { UserService } from '../../user/services/user.service';
import { Issue, IssueTransaction } from '../classes';
import { IssueService } from './issue.service';
import * as moment from 'moment';
import { IssueTypeNamePipe } from '../../shared/pipes/issue-type-name.pipe';
import { SessionService } from '../../shared/services/session.service';
import { User } from '../../user/classes';

@Injectable({
  providedIn: 'root'
})
export class NotificationPlaceholderReplacementService {

  static placeholderCodes = {
    CUSTOMERNAME: /\$customername/g,
    CUSTOMERNUMBER: /\$customernumber/g,
    DATETIME: /\$datetime/g,
    DATE: /\$date/g,
    EDITOR: /\$editor/g,
    ISSUE_CREATION: /\$issuecreation/g,
    ISSUE_CREATOR: /\$issuecreator/g,
    ISSUE_CUSTOMER: /\$issuecustomer/g,
    ISSUE_DESC: /\$issuedesc/g,
    ISSUE_ESCALATION: /\$issueescalation/g,
    ISSUE_LINK: /\$issuelink/g,
    ISSUE_NUMBER: /\$issuenumber/g,
    ISSUE_TITLE: /\$issuetitle/g,
    ISSUE_TYPE: /\$issuetype/g,
    ISSUE_STATUS: /\$issuestatus/g,
    TRANSACTIONTITLE: /\$transactiontitle/g,
    TRANSACTIONRESULTSTATUS: /\$transactionresultstate/g,
  };

  constructor(
    private _customerService: CustomerService,
    private _issueService: IssueService,
    private _userService: UserService,
    private _sessionService: SessionService
  ) { }

  replace(notificationTask: NotificationTask, issue: Issue = null, issueTransaction: IssueTransaction = null): Observable<NotificationTask> {

    if (!notificationTask || !notificationTask.content) return of(null);

    var replacements: { key: RegExp, value: string }[] = [
      { key: NotificationPlaceholderReplacementService.placeholderCodes.CUSTOMERNAME, value: null },
      { key: NotificationPlaceholderReplacementService.placeholderCodes.CUSTOMERNUMBER, value: null },
      { key: NotificationPlaceholderReplacementService.placeholderCodes.ISSUE_CREATOR, value: null },
      { key: NotificationPlaceholderReplacementService.placeholderCodes.ISSUE_CUSTOMER, value: null },
      { key: NotificationPlaceholderReplacementService.placeholderCodes.ISSUE_STATUS, value: null },
      { key: NotificationPlaceholderReplacementService.placeholderCodes.TRANSACTIONRESULTSTATUS, value: null },
      { key: NotificationPlaceholderReplacementService.placeholderCodes.EDITOR, value: null },
    ], obs = [
      this._sessionService.user$.pipe(take(1), tap((user: User) => {
        replacements.find(y => y.key === NotificationPlaceholderReplacementService.placeholderCodes.EDITOR).value = user.userName;
      })) as Observable<any>
    ];

    if(!notificationTask.subject) notificationTask.subject = '';
    if(!notificationTask.content) notificationTask.content = '';

    if (notificationTask.content.match(NotificationPlaceholderReplacementService.placeholderCodes.DATETIME)) {
      notificationTask.subject = notificationTask.subject.replace(NotificationPlaceholderReplacementService.placeholderCodes.DATETIME, `${moment().format('DD.MM.YYYY HH:mm')} Uhr`);
      notificationTask.content = notificationTask.content.replace(NotificationPlaceholderReplacementService.placeholderCodes.DATETIME, `${moment().format('DD.MM.YYYY HH:mm')} Uhr`);
    }
    if (notificationTask.content.match(NotificationPlaceholderReplacementService.placeholderCodes.DATE)) {
      notificationTask.subject = notificationTask.subject.replace(NotificationPlaceholderReplacementService.placeholderCodes.DATE, moment().format('DD.MM.YYYY'));
      notificationTask.content = notificationTask.content.replace(NotificationPlaceholderReplacementService.placeholderCodes.DATE, moment().format('DD.MM.YYYY'));
    }

    if (issue) {
      if (issue.issuecreator && notificationTask.content.match(NotificationPlaceholderReplacementService.placeholderCodes.ISSUE_CREATOR)) obs.push(this._userService.getUserById(issue.issuecreator, true).pipe(take(1), tap(x => replacements.find(y => y.key === NotificationPlaceholderReplacementService.placeholderCodes.ISSUE_CREATOR).value = x.userName)));
      if (issue.issuenumber && notificationTask.content.match(NotificationPlaceholderReplacementService.placeholderCodes.ISSUE_NUMBER)) {
        notificationTask.subject = notificationTask.subject.replace(NotificationPlaceholderReplacementService.placeholderCodes.ISSUE_NUMBER, issue.issuenumber.toString());
        notificationTask.content = notificationTask.content.replace(NotificationPlaceholderReplacementService.placeholderCodes.ISSUE_NUMBER, issue.issuenumber.toString());
      }
      if (issue.issuecreation && notificationTask.content.match(NotificationPlaceholderReplacementService.placeholderCodes.ISSUE_CREATION)) {
        notificationTask.subject = notificationTask.subject.replace(NotificationPlaceholderReplacementService.placeholderCodes.ISSUE_CREATION, moment(issue.issuecreation).format('DD.MM.YYYY HH:mm'));
        notificationTask.content = notificationTask.content.replace(NotificationPlaceholderReplacementService.placeholderCodes.ISSUE_CREATION, moment(issue.issuecreation).format('DD.MM.YYYY HH:mm'));
      }
      if (notificationTask.content.match(NotificationPlaceholderReplacementService.placeholderCodes.ISSUE_ESCALATION)) {
        notificationTask.subject = notificationTask.subject.replace(NotificationPlaceholderReplacementService.placeholderCodes.ISSUE_ESCALATION, issue.issueescalation ? moment(issue.issueescalation).format('DD.MM.YYYY HH:mm') : 'kein Wunschdatum');
        notificationTask.content = notificationTask.content.replace(NotificationPlaceholderReplacementService.placeholderCodes.ISSUE_ESCALATION, issue.issueescalation ? moment(issue.issueescalation).format('DD.MM.YYYY HH:mm') : 'kein Wunschdatum');
      }
      if (issue.issuetitle && notificationTask.content.match(NotificationPlaceholderReplacementService.placeholderCodes.ISSUE_TITLE)) {
        notificationTask.subject = notificationTask.subject.replace(NotificationPlaceholderReplacementService.placeholderCodes.ISSUE_TITLE, issue.issuetitle.toString());
        notificationTask.content = notificationTask.content.replace(NotificationPlaceholderReplacementService.placeholderCodes.ISSUE_TITLE, issue.issuetitle.toString());
      }
      if (notificationTask.content.match(NotificationPlaceholderReplacementService.placeholderCodes.ISSUE_DESC)) {
        notificationTask.subject = notificationTask.subject.replace(NotificationPlaceholderReplacementService.placeholderCodes.ISSUE_DESC, issue.issuedesc ? issue.issuedesc : 'keine Beschreibung');
        notificationTask.content = notificationTask.content.replace(NotificationPlaceholderReplacementService.placeholderCodes.ISSUE_DESC, issue.issuedesc ? issue.issuedesc : 'keine Beschreibung');
      }
      if (issue.issueid && notificationTask.content.match(NotificationPlaceholderReplacementService.placeholderCodes.ISSUE_LINK)) {
        notificationTask.subject = notificationTask.subject.replace(NotificationPlaceholderReplacementService.placeholderCodes.ISSUE_LINK, `${NotificationPlaceholderReplacementService.issueDetailsLocation}${issue.issueid}`);
        notificationTask.content = notificationTask.content.replace(NotificationPlaceholderReplacementService.placeholderCodes.ISSUE_LINK, `${NotificationPlaceholderReplacementService.issueDetailsLocation}${issue.issueid}`);
      }
      if (notificationTask.content.match(NotificationPlaceholderReplacementService.placeholderCodes.ISSUE_TITLE)) {
        notificationTask.subject = notificationTask.subject.replace(NotificationPlaceholderReplacementService.placeholderCodes.ISSUE_TITLE, issue?.issuetitle?.toString());
        notificationTask.content = notificationTask.content.replace(NotificationPlaceholderReplacementService.placeholderCodes.ISSUE_TITLE, issue?.issuetitle?.toString());
      }
      if (typeof issue.issuetype === 'number' && notificationTask.content.match(NotificationPlaceholderReplacementService.placeholderCodes.ISSUE_TYPE)) {
        let pipe = new IssueTypeNamePipe();
        notificationTask.subject = notificationTask.subject.replace(NotificationPlaceholderReplacementService.placeholderCodes.ISSUE_TYPE, pipe.transform(issue.issuetype));
        notificationTask.content = notificationTask.content.replace(NotificationPlaceholderReplacementService.placeholderCodes.ISSUE_TYPE, pipe.transform(issue.issuetype));
      }
      if (typeof issue.issuestate === 'number' && notificationTask.content.match(NotificationPlaceholderReplacementService.placeholderCodes.ISSUE_STATUS)) obs.push(this._issueService.getIssueStatusById(issue.issuestate, true).pipe(take(1), tap(x => replacements.find(y => y.key === NotificationPlaceholderReplacementService.placeholderCodes.ISSUE_STATUS).value = x.statename)));
      if (typeof issue.issuecustomer === 'number') {
        obs.push(
          this._customerService.getCustomerById(issue.issuecustomer, true).pipe(take(1), tap(x => {
            replacements.find(y => y.key === NotificationPlaceholderReplacementService.placeholderCodes.CUSTOMERNAME).value = x.customername;
            replacements.find(y => y.key === NotificationPlaceholderReplacementService.placeholderCodes.CUSTOMERNUMBER).value = x.customernumber;
            replacements.find(y => y.key === NotificationPlaceholderReplacementService.placeholderCodes.ISSUE_CUSTOMER).value = [x.customernumber, x.customername].filter(x => x).join(' ');
          }))
        );
      } else {
        replacements.find(y => y.key === NotificationPlaceholderReplacementService.placeholderCodes.CUSTOMERNAME).value = 'kein Kunde';
        replacements.find(y => y.key === NotificationPlaceholderReplacementService.placeholderCodes.CUSTOMERNUMBER).value = 'kein Kunde';
        replacements.find(y => y.key === NotificationPlaceholderReplacementService.placeholderCodes.ISSUE_CUSTOMER).value = 'kein Kunde';
      }
    }

    if (issueTransaction) {
      if (issueTransaction.name) {
        notificationTask.subject = notificationTask.subject.replace(NotificationPlaceholderReplacementService.placeholderCodes.TRANSACTIONTITLE, issueTransaction.name);
        notificationTask.content = notificationTask.content.replace(NotificationPlaceholderReplacementService.placeholderCodes.TRANSACTIONTITLE, issueTransaction.name);
      }
      if (typeof issueTransaction.result === 'number') {
        obs.push(this._issueService.getIssueStatusById(issueTransaction.result, true).pipe(take(1), tap(x => {
          replacements.find(y => y.key === NotificationPlaceholderReplacementService.placeholderCodes.TRANSACTIONRESULTSTATUS).value = x.statename;
        })));
      }
      else {
        replacements.find(y => y.key === NotificationPlaceholderReplacementService.placeholderCodes.TRANSACTIONRESULTSTATUS).value = 'kein geänderter Status nach Ausführung';
      };
    }

    return forkJoin(obs.length > 0 ? obs : of(null)).pipe(map((_) => {
      replacements.forEach(x => {
        notificationTask.subject = notificationTask.subject.replace(x.key, x.value);
        notificationTask.content = notificationTask.content.replace(x.key, x.value);
      });
      return notificationTask;
    }));
  }

  static issueDetailsLocation = "https://ams.campus-netz.services/client/issue-details/";
}
